/**
 * Like lodash/property, but with better type inference. Great for mapping
 * over an input array to extract an array of members' property values.
 *
 * @example
 * ```ts
 * const people = [
 *   { name: 'Alice', age: 24 },
 *   { name: 'Bob', age: 27 },
 * ]
 *
 * const names = people.map(pluck('name')); // => ['Alice', 'Bob']
 * ```
 */
export default function pluck<T, K extends keyof T>(key: K) {
  return (obj: T): T[K] => obj[key];
}

/**
 * Returns a function to map `pluck` over an array of objects, extracting the
 * values of a given key into a new array.
 *
 * Great for a `.then` callback to pull a property off of a return value.
 *
 * @example
 * ```ts
 * const people = [
 *   { name: 'Alice', age: 24 },
 *   { name: 'Bob', age: 27 },
 * ]
 *
 * const getPeople = async () => people;
 *
 * const names = await getPeople().then(pluckAll('name')); // => ['Alice', 'Bob']
 * ```
 */
export function pluckAll<T, K extends keyof T>(
  key: K
): (arr: Array<T>) => Array<T[K]> {
  return (arr) => arr.map(pluck(key));
}
